import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { lazy, Suspense } from "react";
import ErrorBoundary from "@/components/ErrorBoundary";
import CookieConsent from "@/components/CookieConsent";
import { HelmetProvider } from "react-helmet-async";

// Lazy-loaded pages — each gets its own chunk, loaded on demand
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const ResultsPage = lazy(() => import("@/pages/ResultsPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const LinkedInPage = lazy(() => import("@/pages/usecases/LinkedInPage"));
const CVPage = lazy(() => import("@/pages/usecases/CVPage"));
const DatingPage = lazy(() => import("@/pages/usecases/DatingPage"));
const SocialMediaPage = lazy(() => import("@/pages/usecases/SocialMediaPage"));
const VirksomhedPage = lazy(() => import("@/pages/usecases/VirksomhedPage"));
// Blog
const BlogIndex = lazy(() => import("@/pages/blog/BlogIndex"));
const KompleteGuide = lazy(() => import("@/pages/blog/posts/KompleteGuide"));
const HeadITvsHeadshotsDK = lazy(() => import("@/pages/blog/posts/HeadITvsHeadshotsDK"));
const AIvsPhotographer = lazy(() => import("@/pages/blog/posts/AIvsPhotographer"));
const LinkedInGuide = lazy(() => import("@/pages/blog/posts/LinkedInGuide"));
const GratisGuide = lazy(() => import("@/pages/blog/posts/GratisGuide"));
// By-sider
const KobenhavnPage = lazy(() => import("@/pages/usecases/KobenhavnPage"));
const AarhusPage = lazy(() => import("@/pages/usecases/AarhusPage"));
const OdensePage = lazy(() => import("@/pages/usecases/OdensePage"));
const AalborgPage = lazy(() => import("@/pages/usecases/AalborgPage"));

// Shared loading spinner for Suspense fallback
const PageSpinner = () => (
    <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#0A0A0A] border-t-transparent rounded-full spinner" />
    </div>
);

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <PageSpinner />;
    if (!user) return <Navigate to="/login" replace />;
    return children;
};

// Public route wrapper (redirect if logged in)
const PublicRoute = ({ children }) => {
    const { user, loading } = useAuth();
    if (loading) return <PageSpinner />;
    if (user) return <Navigate to="/dashboard" replace />;
    return children;
};

function AppRoutes() {
    return (
        <Suspense fallback={<PageSpinner />}>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
                <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/results" element={<ProtectedRoute><ResultsPage /></ProtectedRoute>} />
                <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                {/* SEO use-case sider */}
                <Route path="/ai-headshot-til-linkedin" element={<LinkedInPage />} />
                <Route path="/ai-profilbillede-til-cv" element={<CVPage />} />
                <Route path="/ai-headshot-til-dating" element={<DatingPage />} />
                <Route path="/ai-headshot-til-social-media" element={<SocialMediaPage />} />
                <Route path="/virksomhedsbilleder-ai" element={<VirksomhedPage />} />
                {/* By-sider */}
                <Route path="/ai-headshots-koebenhavn" element={<KobenhavnPage />} />
                <Route path="/ai-headshots-aarhus" element={<AarhusPage />} />
                <Route path="/ai-headshots-odense" element={<OdensePage />} />
                <Route path="/ai-headshots-aalborg" element={<AalborgPage />} />
                {/* Blog */}
                <Route path="/blog" element={<BlogIndex />} />
                <Route path="/blog/ai-profilbillede-guide" element={<KompleteGuide />} />
                <Route path="/blog/ai-headshot-eller-fotograf" element={<AIvsPhotographer />} />
                <Route path="/blog/linkedin-profilbillede-guide" element={<LinkedInGuide />} />
                <Route path="/blog/gratis-ai-profilbillede" element={<GratisGuide />} />
                <Route path="/blog/vaelg-ai-headshot-loesning" element={<HeadITvsHeadshotsDK />} />
                {/* Redirects fra gamle slugs */}
                <Route path="/blog/ai-profilbillede-komplet-guide" element={<Navigate to="/blog/ai-profilbillede-guide" replace />} />
                <Route path="/blog/headit-vs-headshots-dk" element={<Navigate to="/blog/vaelg-ai-headshot-loesning" replace />} />
                <Route path="/blog/ai-headshot-vs-fotograf" element={<Navigate to="/blog/ai-headshot-eller-fotograf" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <ErrorBoundary>
                    <AuthProvider>
                        <AppRoutes />
                        <Toaster position="top-right" richColors />
                        <CookieConsent />
                    </AuthProvider>
                </ErrorBoundary>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
